import { FC, useEffect, useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { Text } from '@components/Text';
import { RedesignButton } from '@components/RedesignButton';
import calendarIcon from 'src/redesign/assets/calendar-icon.svg';
import timeIcon from 'src/redesign/assets/time-icon.svg';
import locationIcon from 'src/redesign/assets/location-icon.svg';
import { DownloadIcon, EyeIcon, TicketIcon, SendIcon } from 'src/redesign/assets/icons';
import { Tag } from 'primereact/tag';
import c from 'clsx';
import styles from 'src/redesign/features/AttendeesSidebar/views/OrderSidebarView/components/OrderCard/OrderCard.module.scss';
import { Divider } from 'primereact/divider';
import { getPaymentLogo } from 'src/redesign/shared/utils/getPaymentLogo';
import {
  cancelAndRefundAttendee,
  fetchCartRequests,
  fetchTicketPdf,
  fetchTicketReceiptPdf,
  transferAttendeeTicket,
} from 'src/redesign/features/Attendees/store/actions';
import {
  cartRequestsLoadingSelector,
  cartRequestsSelector,
} from 'src/redesign/features/Attendees/store/selectors';
import { setCurrentModalAction } from 'src/redesign/features/Modals/store/reducer';
import { CONNECTED_MODALS } from 'src/redesign/features/Modals/store/constants';
import { toastNotificationsSlice } from 'src/redesign/features/Toast/store/reducer';
import moment from 'moment';
import { Dialog } from 'primereact/dialog';
import { Carousel } from 'primereact/carousel';
import appleStoreIcon from 'src/redesign/assets/apple-store-icon.svg';
import playStoreIcon from 'src/redesign/assets/play-store-icon.svg';
import { Tooltip } from 'primereact/tooltip';
import { Ticket } from 'src/types/types';
import { API_INSTANCE } from 'src/features/utils';
import S from './AttendeeTicketDetailsPage.module.scss';

// QR Code carousel template (reused from sidebar)
const QrCodeTemplate: FC<{ ticket: Ticket }> = ({ ticket }) => {
  return (
    <div className={S.qrCodeCard}>
      <div className={S.attendeeBadge}>
        <i className="pi pi-user" />
        <Text bold>
          {ticket.first_name} {ticket.last_name}
        </Text>
      </div>

      <div className={S.qrCodeContainer}>
        <div className={S.scanCorners}>
          <div className={S.corner} style={{ top: 0, left: 0 }} />
          <div className={S.corner} style={{ top: 0, right: 0 }} />
          <div className={S.corner} style={{ bottom: 0, left: 0 }} />
          <div className={S.corner} style={{ bottom: 0, right: 0 }} />
        </div>
        <img src={ticket.qrcode} alt="Ticket QR Code" className={S.qrCodeImage} />
      </div>

      <Text color="black-400" className={S.scanInstructions}>
        <i className="pi pi-qrcode" style={{ marginRight: '8px' }} />
        Scan this code at the entrance
      </Text>
    </div>
  );
};

export const AttendeeTicketDetailsPage: FC = () => {
  const { cartId } = useParams<{ cartId: string }>();
  const [showQrCodeDialog, setShowQrCodeDialog] = useState(false);
  const [orderDetailsExpanded, setOrderDetailsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [ticketInfo, setTicketInfo] = useState<any>(null);
  const cartRequests = useAppSelector(cartRequestsSelector);
  const cartRequestsLoading = useAppSelector(cartRequestsLoadingSelector);
  const dispatch = useAppDispatch();
  const history = useHistory();

  // Check if we're in frame mode (no layout)
  const isFrameMode = window.location.href.includes('/order-frame');

  // Fetch ticket data from API on mount or when cartId changes
  useEffect(() => {
    const fetchTicketData = async () => {
      if (!cartId) return;

      setIsLoading(true);
      try {
        const response = await API_INSTANCE.get(
          `/api/v1/dashboard/attendee-portal/tickets/${cartId}`,
        );

        // Store in local state
        setTicketInfo(response.data);
      } catch (error) {
        console.error('Error fetching ticket data:', error);
        dispatch(
          toastNotificationsSlice.actions.showNotification({
            severity: 'error',
            detail: 'Failed to load ticket details. Please try again.',
            life: 5000,
          }),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchTicketData();
  }, [cartId, dispatch]);

  useEffect(() => {
    if (ticketInfo?.cart_id && !cartRequests) {
      dispatch(fetchCartRequests(ticketInfo.cart_id));
    }
  }, [ticketInfo?.cart_id, dispatch, cartRequests]);

  // Send iframe dimensions to parent window after content is rendered (frame mode only)
  useEffect(() => {
    if (isFrameMode && ticketInfo && !isLoading) {
      const sendDimensions = async () => {
        // Wait for next frame to ensure DOM is fully rendered
        await new Promise((resolve) => requestAnimationFrame(resolve));

        // Get the actual content height
        const contentHeight = document.body.scrollHeight;

        // Send message to parent window
        window.parent.postMessage(
          {
            type: 'ticketframe:resize',
            height: contentHeight,
            cartId: ticketInfo.cart_id,
          },
          '*',
        ); // Use '*' or specific origin if known

        console.log(`Sent iframe dimensions to parent: ${contentHeight}px`);
      };

      sendDimensions();
    }
  }, [isFrameMode, ticketInfo, isLoading]);

  if (isLoading || !ticketInfo) {
    return (
      <div className={S.pageContainer}>
        {!isFrameMode && (
          <div className={S.header}>
            <i
              className="pi pi-arrow-left"
              style={{ fontSize: '1.5rem', cursor: 'pointer' }}
              onClick={() => history.goBack()}
            />
            <Text variation="h3">Ticket Details</Text>
          </div>
        )}
        <div
          className={S.content}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '3rem',
          }}
        >
          <i
            className="pi pi-spin pi-spinner"
            style={{ fontSize: '3rem', color: '#9c26dc', marginBottom: '1rem' }}
          />
          <Text variation="h5" color="black-400">
            Loading ticket details...
          </Text>
        </div>
      </div>
    );
  }

  const {
    cart_id,
    friendly_cart_id,
    purchase_date,
    total,
    refund_amount,
    tickets,
    payment_method,
    card_type,
    last4,
    status,
  } = ticketInfo;

  // Check for pending requests
  const pendingRequests = cartRequests?.filter((request) => request.status === 'pending') || [];
  const hasPendingRequests = pendingRequests.length > 0;

  // Check if user can access tickets/documents (only attending or checkedIn status)
  const canAccessTickets = status === 'attending' || status === 'checkedIn';

  // Check if user can cancel or transfer (only attending status)
  const canCancelOrTransfer = status === 'attending';

  // Format purchase date
  const purchaseDate = moment(purchase_date).format('dddd, D MMMM YYYY h:mm A');

  // Get payment logo
  const paymentLogoComponent = getPaymentLogo(card_type?.toLowerCase() || '');

  // Handle cancel request
  const handleCancel = async (data: { refund: boolean; reason: string }) => {
    try {
      await dispatch(
        cancelAndRefundAttendee({
          cart_id,
          requestRefund: data.refund,
          reason: data.reason,
        }),
      );

      const message = data.refund
        ? 'You have been marked as not attending and a refund request has been sent to the host for review.'
        : 'You have been successfully marked as not attending.';

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail: message,
          life: 5000,
        }),
      );
    } catch (error) {
      console.error('Cancel failed:', error);
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to process cancellation. Please try again.',
          life: 5000,
        }),
      );
    }
  };

  // Handle transfer request
  const handleTransfer = async (data: {
    eventId: string;
    occurrenceId?: string;
    reason: string;
  }) => {
    try {
      await dispatch(
        transferAttendeeTicket({
          cart_id,
          targetEventId: data.eventId,
          reason: data.reason,
        }),
      );

      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'success',
          detail:
            'Your transfer request has been sent to the host for approval. You will be notified of their decision.',
          life: 5000,
        }),
      );
    } catch (error) {
      console.error('Transfer failed:', error);
      dispatch(
        toastNotificationsSlice.actions.showNotification({
          severity: 'error',
          detail: 'Failed to submit transfer request. Please try again.',
          life: 5000,
        }),
      );
    }
  };

  // Open cancel/transfer modal
  const openCancelTransferModal = () => {
    dispatch(
      setCurrentModalAction({
        currentModal: CONNECTED_MODALS.CANCEL_TRANSFER_MODAL,
        data: {
          title: 'Cancel or Transfer Event',
          ticketInfo,
          onCancel: handleCancel,
          onTransfer: handleTransfer,
        },
      }),
    );
  };

  // Open contact host modal
  const openContactHostModal = () => {
    dispatch(
      setCurrentModalAction({
        currentModal: CONNECTED_MODALS.CONTACT_HOST_MODAL,
        data: {
          title: 'Contact Host',
          ticketInfo,
        },
      }),
    );
  };

  return (
    <>
      <Dialog
        header={
          <div className={S.dialogHeader}>
            <i className="pi pi-ticket" style={{ fontSize: '1.5rem' }} />
            <span>View QR Code</span>
            {tickets.length > 1 && (
              <span className={S.ticketCount}>
                {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        }
        visible={showQrCodeDialog}
        onHide={() => setShowQrCodeDialog(false)}
        className={S.qrCodeDialog}
        style={{ width: '90%', maxWidth: '500px' }}
        draggable={false}
        resizable={false}
        closeOnEscape
      >
        {tickets.length > 0 &&
          (tickets.length === 1 ? (
            <QrCodeTemplate ticket={tickets[0]} />
          ) : (
            <Carousel
              value={tickets}
              numVisible={1}
              numScroll={1}
              itemTemplate={(ticket) => <QrCodeTemplate ticket={ticket} />}
              showIndicators
              showNavigators={true}
              circular
              className={S.qrCarousel}
            />
          ))}
      </Dialog>

      <div className={S.pageContainer}>
        {/* Header with back button (hidden in frame mode) */}
        {!isFrameMode && (
          <div className={S.header}>
            <i
              className="pi pi-arrow-left"
              style={{ fontSize: '1.5rem', cursor: 'pointer' }}
              onClick={() => history.goBack()}
            />
            <Text variation="h3">Ticket Details</Text>
          </div>
        )}

        <div className={S.content}>
          {/* Order Details Accordion (hidden in frame mode) */}
          {!isFrameMode && (
            <div className={S.section}>
              <div
                className={S.accordionHeader}
                onClick={() => setOrderDetailsExpanded(!orderDetailsExpanded)}
              >
                <div className={S.accordionHeaderLeft}>
                  <i
                    className="pi pi-shopping-cart"
                    style={{ fontSize: '1rem', color: '#9c26dc' }}
                  />
                  <div>
                    <Text variation="body-small" bold color="black-400">
                      ORDER DETAILS
                    </Text>
                    <Text variation="caption" color="black-400">
                      #{friendly_cart_id}
                    </Text>
                  </div>
                </div>
                <div className={S.accordionHeaderRight}>
                  <i
                    className={`pi pi-chevron-down ${orderDetailsExpanded ? S.chevronExpanded : ''}`}
                    style={{ fontSize: '0.875rem', color: '#9c26dc', marginLeft: '8px' }}
                  />
                </div>
              </div>

              {orderDetailsExpanded && (
                <div className={S.accordionContent}>
                  <div className={S.infoRow}>
                    <Text variation="body-small" color="black-100">
                      Purchased Datess
                    </Text>
                    <Text variation="h5">{purchaseDate}</Text>
                  </div>
                  <div className={S.infoRowGrid}>
                    <div>
                      <Text variation="body-small" color="black-100">
                        Tickets
                      </Text>
                      <Text variation="h5">
                        {tickets.length} ticket{tickets.length !== 1 ? 's' : ''}
                      </Text>
                    </div>
                    <div>
                      <Text variation="body-small" color="black-100">
                        Total
                      </Text>
                      <Text variation="h5">
                        {ticketInfo.currency_symbol}
                        {total.toFixed(2)}
                      </Text>
                    </div>
                    <div>
                      <Text variation="body-small" color="black-100">
                        Refunds
                      </Text>
                      <Text variation="h5">
                        {ticketInfo.currency_symbol}
                        {refund_amount.toFixed(2)}
                      </Text>
                    </div>
                  </div>
                  {payment_method !== 'N/A' && (
                    <div className={S.infoRow}>
                      <Text variation="body-small" color="black-100">
                        Payment Details
                      </Text>
                      <div className={S.paymentInfo}>
                        {paymentLogoComponent && <div>{paymentLogoComponent}</div>}
                        <Text>
                          {card_type !== 'N/A'
                            ? `${card_type} (${last4 ? `** ${last4}` : ''})`
                            : payment_method}
                        </Text>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Tickets Section - Only visible for attending/checkedIn status */}
          {canAccessTickets && (
            <div className={S.section}>
              <div className={S.sectionHeader}>
                <i className="pi pi-ticket" style={{ fontSize: '1rem', color: '#9c26dc' }} />
                <Text variation="body-small" bold color="black-400">
                  TICKETS
                </Text>
              </div>
              {tickets.some((ticket) => ticket.qrcode) && (
                <>
                  <RedesignButton
                    variation="primary"
                    height={52}
                    className={S.fullWidthButton}
                    onClick={() => setShowQrCodeDialog(true)}
                  >
                    <TicketIcon />
                    <Text regular color="white-100">
                      View QR Code
                    </Text>
                  </RedesignButton>
                  {/* Wallet Icons */}
                  <div className={S.walletButtons}>
                    {tickets[0]?.google_wallet_link && (
                      <>
                        <Tooltip
                          target=".mobile-google-wallet"
                          content="Add to Google Wallet"
                          position="top"
                        />
                        <a
                          href={tickets[0].google_wallet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${S.walletIcon} ${S.googleWallet} mobile-google-wallet`}
                        >
                          <img src={playStoreIcon} alt="Google Wallet" />
                        </a>
                      </>
                    )}
                    {tickets[0]?.apple_wallet_link && (
                      <>
                        <Tooltip
                          target=".mobile-apple-wallet"
                          content="Add to Apple Wallet"
                          position="top"
                        />
                        <a
                          href={tickets[0].apple_wallet_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${S.walletIcon} ${S.appleWallet} mobile-apple-wallet`}
                        >
                          <img src={appleStoreIcon} alt="Apple Wallet" />
                        </a>
                      </>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Documents Section - Only visible for attending/checkedIn status */}
          {canAccessTickets && (
            <div className={S.section}>
              <div className={S.sectionHeader}>
                <i className="pi pi-file" style={{ fontSize: '1rem', color: '#9c26dc' }} />
                <Text variation="body-small" bold color="black-400">
                  DOCUMENTS
                </Text>
              </div>
              <div className={S.documentButtons}>
                <RedesignButton
                  variation="secondary"
                  height={52}
                  onClick={() => dispatch(fetchTicketReceiptPdf(cart_id))}
                >
                  <EyeIcon />
                  <Text regular color="brand-400">
                    View Receipt
                  </Text>
                </RedesignButton>
                <RedesignButton
                  variation="secondary"
                  height={52}
                  onClick={() => dispatch(fetchTicketPdf(cart_id))}
                >
                  <DownloadIcon />
                  <Text regular color="brand-400">
                    Download PDF
                  </Text>
                </RedesignButton>
              </div>
            </div>
          )}

          {/* Actions Section */}
          <div className={S.section}>
            <div className={S.sectionHeader}>
              <i className="pi pi-cog" style={{ fontSize: '1rem', color: '#9c26dc' }} />
              <Text variation="body-small" bold color="black-400">
                ACTIONS
              </Text>
            </div>
            <div className={S.actionLinks}>
              {/* Cancel or Transfer - Only visible for attending status */}
              {canCancelOrTransfer && (
                <div
                  onClick={hasPendingRequests ? undefined : openCancelTransferModal}
                  className={hasPendingRequests ? S.disabledLink : S.actionLink}
                >
                  <i className="pi pi-refresh" />
                  <Text regular color={hasPendingRequests ? 'black-400' : 'brand-400'}>
                    {hasPendingRequests ? 'Request Pending' : 'Cancel or Transfer'}
                  </Text>
                </div>
              )}
              <div onClick={openContactHostModal} className={S.actionLink}>
                <i className="pi pi-envelope" />
                <Text regular color="brand-400">
                  Contact Host
                </Text>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
